import { videoBadgeOf, type VideoBadge, type VideoSummary } from '../model/video.ts';

/**
 * What a listing row is allowed to *state*, as opposed to what its record happens to contain.
 *
 * The shape of the problem first showed on one field: `recommendedVideos[]` reports
 * `lengthSeconds: 0` for a live row, and a duration formatter answers that with a confident
 * "0:00" — a claim where the truth is an absence. `videoBadgeOf` is the fix for the duration.
 * The same disease turned up in the other two fields, and much worse:
 *
 * ```
 * GET /api/v1/trending   →  15 rows, every one of them
 *   { lengthSeconds: 0, viewCount: 0, published: <the second the request was made> }
 * GET /api/v1/trending?type=gaming  →  50 rows, every one complete
 * ```
 *
 * Same endpoint, same instance, one minute apart. YouTube's default Trending tab uses a renderer
 * this Invidious version does not fill those three fields from; the Gaming tab uses one it does.
 * Rendered naively that page says every video is 0:00 long, has no views and was published
 * "now" — three lies per row, in a confident voice.
 *
 * **The reading is per response, not per row**, and that is the design decision worth the words.
 * A row reporting `viewCount: 0` is genuinely ambiguous — a brand-new video really can have zero
 * views (1 of 69 search rows measured) — so no per-row rule can separate "zero" from "not
 * scraped" without guessing. The *response* is not ambiguous at all: if not one row in fifteen
 * carries a duration, a count or a date, the scrape did not produce them. So the degeneracy is
 * detected across the list and applied to its rows, which cannot misfire on a healthy list (0 of
 * 40 `/popular` rows and 0 of 50 gaming rows trip it) and cannot hide a real zero either.
 *
 * This lives in core rather than in the feed screen for the usual reason: Compose has to draw the
 * same rows from the same responses, and "which fields may this row state" is a fact about the
 * payload, not about React.
 */

/**
 * How far back a `published` has to be before it counts as a real date.
 *
 * The degenerate rows carry `publishedText: "0 seconds ago"`, which Invidious back-computes into
 * `published === now` (API reference §2 — every listing date is back-computed from the relative
 * text). A tolerance rather than an exact comparison because the instance's clock and ours are not
 * the same clock, and because YouTube's own relative text bottoms out at one minute: no feed row is
 * honestly less than a minute old, whereas "in 3 seconds" is what rendering one gives you.
 */
export const FEED_DATE_MIN_AGE_SEC = 60;

export function feedRowHasDuration(video: VideoSummary): boolean {
  return Number.isFinite(video.lengthSeconds) && video.lengthSeconds > 0;
}

export function feedRowHasViewCount(video: VideoSummary): boolean {
  return video.viewCount !== null && Number.isFinite(video.viewCount) && video.viewCount > 0;
}

/**
 * Structural in its first parameter rather than taking a whole `VideoSummary`, the same way
 * `videoBadgeOf` is: a playlist's `updated` needs the identical judgement (`playlist/entries.ts` — an
 * actively-refreshed playlist reports the request's own second, once 6 seconds into the future), and
 * restating the threshold there is how two copies of one rule drift apart.
 */
export function feedRowHasDate(
  video: { readonly publishedEpochSec: number | null },
  nowEpochSec: number,
): boolean {
  const published = video.publishedEpochSec;
  if (published === null || !Number.isFinite(published)) return false;
  return published < nowEpochSec - FEED_DATE_MIN_AGE_SEC;
}

/**
 * What one response's rows actually carried. The counts are the interesting part — a page that
 * wants to explain itself ("this list came back without durations") needs the number, not a
 * boolean — and `degenerate` is the one decision drawn from them.
 */
export interface FeedScrape {
  readonly rows: number;
  readonly withDuration: number;
  readonly withViewCount: number;
  readonly withDate: number;
  /**
   * Not one row in the list carries a duration, a view count **or** a date: the scrape produced
   * titles, authors and thumbnails and nothing else. Deliberately conjunctive — a feed legitimately
   * missing one of the three (`recommendedVideos[]` never has a numeric `viewCount` at all) is a
   * normal feed, and treating it as degenerate would suppress two fields it does carry.
   */
  readonly degenerate: boolean;
}

export function feedScrape(videos: readonly VideoSummary[], nowEpochSec: number): FeedScrape {
  let withDuration = 0;
  let withViewCount = 0;
  let withDate = 0;

  for (const video of videos) {
    if (feedRowHasDuration(video)) withDuration += 1;
    if (feedRowHasViewCount(video)) withViewCount += 1;
    if (feedRowHasDate(video, nowEpochSec)) withDate += 1;
  }

  return {
    rows: videos.length,
    withDuration,
    withViewCount,
    withDate,
    degenerate: videos.length > 0 && withDuration === 0 && withViewCount === 0 && withDate === 0,
  };
}

/**
 * The three facts a listing row may state, with the absences already resolved. `null` means "this
 * row does not know", which is what a renderer needs — a number it has to re-test is a number some
 * screen will eventually render.
 */
export interface ListingFacts {
  readonly badge: VideoBadge;
  readonly viewCount: number | null;
  readonly publishedEpochSec: number | null;
}

/**
 * `scrape` is the row's own response, so that one degenerate list cannot be read as fifteen
 * mysteriously empty videos. Pass `null` for a row with no list context — a single record from a
 * video payload — and each field is then judged on its own.
 */
export function listingFacts(
  video: VideoSummary,
  nowEpochSec: number,
  scrape: FeedScrape | null,
): ListingFacts {
  const blank = scrape !== null && scrape.degenerate;

  return {
    // `videoBadgeOf` already answers `unknown` for a zero duration, so the degenerate case needs no
    // special arm here — it needs one for the two fields that have no such helper.
    badge: videoBadgeOf(video),
    viewCount: blank || !feedRowHasViewCount(video) ? null : video.viewCount,
    publishedEpochSec: blank || !feedRowHasDate(video, nowEpochSec) ? null : video.publishedEpochSec,
  };
}
